import { useEffect, useState } from "react";
import { Download, Share2, PartyPopper, Facebook } from "lucide-react";
import { toast } from "sonner";
import { API, api, SITE } from "@/lib/api";

const imageUrl = (id) => `${API}/celebrations/${id}/image`;
const shareLink = (id) => `${window.location.origin}/celebrations#c-${id}`;
const fileName = (c) => `happy-birthday-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.${c.content_type.split("/")[1].replace("jpeg", "jpg")}`;

async function fetchFile(c) {
  const res = await fetch(imageUrl(c.id));
  const blob = await res.blob();
  return new File([blob], fileName(c), { type: c.content_type });
}

async function downloadGraphic(c) {
  try {
    const file = await fetchFile(c);
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed. Please try again.");
  }
}

async function shareGraphic(c) {
  const text = `Happy Birthday, ${c.name}! From the ${SITE.brandFull}`;
  try {
    const file = await fetchFile(c);
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: `Happy Birthday ${c.name}`, text });
      return;
    }
    if (navigator.share) {
      await navigator.share({ title: `Happy Birthday ${c.name}`, text, url: shareLink(c.id) });
      return;
    }
    await navigator.clipboard.writeText(shareLink(c.id));
    toast.success("Link copied, ready to paste and share!");
  } catch (e) {
    if (e?.name !== "AbortError") toast.error("Sharing isn't available here. Try Download instead.");
  }
}

function CelebrationCard({ c, index }) {
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink(c.id))}`;
  return (
    <article
      id={`c-${c.id}`}
      data-testid={`celebration-card-${c.id}`}
      className="group bg-white border border-[#E5E1DA] overflow-hidden animate-[fadeUp_.6s_ease_both]"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="overflow-hidden bg-[#1e4d5d]">
        <img
          src={imageUrl(c.id)}
          alt={`Happy Birthday ${c.name}`}
          loading="lazy"
          className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="p-6">
        <p className="overline text-[#d4a373]">Happy Birthday</p>
        <h3 className="mt-2 text-3xl text-[#1e4d5d]" style={{ fontFamily: "Playfair Display, serif" }} data-testid="celebration-name">
          {c.name}
        </h3>
        {c.message && <p className="mt-3 text-[#2c5865]/80 leading-relaxed" data-testid="celebration-message">{c.message}</p>}
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => shareGraphic(c)} className="btn-primary flex items-center gap-2" data-testid={`celebration-share-${c.id}`}>
            <Share2 size={16} /> Share
          </button>
          <button
            onClick={() => downloadGraphic(c)}
            className="flex items-center gap-2 px-5 py-3 border border-[#1e4d5d] text-[#1e4d5d] text-sm tracking-wide hover:bg-[#1e4d5d] hover:text-white transition-colors"
            data-testid={`celebration-download-${c.id}`}
          >
            <Download size={16} /> Download
          </button>
          <a
            href={fb}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            className="flex items-center justify-center w-12 border border-[#E5E1DA] text-[#1e4d5d] hover:border-[#d4a373] hover:text-[#d4a373] transition-colors"
            data-testid={`celebration-facebook-${c.id}`}
          >
            <Facebook size={16} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Celebrations() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/celebrations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items?.length && window.location.hash) {
      document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [items]);

  return (
    <div data-testid="celebrations-page">
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #e8c79a 0%, #d4a373 35%, #2c5865 75%, #1e4d5d 100%)" }}>
        <div className="container-edge py-24 md:py-32 text-white">
          <p className="overline text-white/90 flex items-center gap-2"><PartyPopper size={14} /> Celebrations</p>
          <h1 className="hero-headline mt-4 text-4xl sm:text-5xl lg:text-6xl max-w-3xl text-white" style={{ fontFamily: "Playfair Display, serif", color: "#ffffff", textShadow: "0 2px 18px rgba(30,77,93,.35)" }}>
            Pop the champagne, <em className="text-[#f8f5ee]">it's your day.</em>
          </h1>
          <p className="mt-6 max-w-xl text-white text-base md:text-lg font-light" style={{ textShadow: "0 1px 10px rgba(30,77,93,.35)" }}>
            We celebrate every move, and every birthday too. Here's a toast to our friends, clients, and
            community. Find your graphic, then share or download it.
          </p>
        </div>
      </section>

      <section className="section bg-[#f8f5ee]">
        <div className="container-edge">
          {items === null && <p className="text-[#2c5865]" data-testid="celebrations-loading">Loading celebrations…</p>}
          {items?.length === 0 && (
            <p className="text-[#2c5865]" data-testid="celebrations-empty">New celebrations are coming soon. Check back!</p>
          )}
          {items?.length > 0 && (
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="celebrations-grid">
              {items.map((c, i) => <CelebrationCard key={c.id} c={c} index={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
